import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

const ChatRestrictionSettings = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);

  const roles = ["user", "vip_gold", "vip_platinum", "vip_diamond", "mod", "admin"];

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
  
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/roles/chat-restrictions",
      });
  
      const configsData = Array.isArray(res.data) ? res.data : [];
      setConfigs(configsData);
      setGlobalEnabled(configsData.every((config) => config.enabled));
      setLoading(false);
    } catch (err) {
      console.error("Lỗi khi tải cấu hình giới hạn chat:", err);
      let errorMessage = "Không thể tải cấu hình giới hạn chat!";
      if (err.response) {
        if (err.response.status === 500) {
          errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
        } else if (err.response.status === 401 || err.response.status === 403) {
          errorMessage = "Phiên làm việc hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại!";
          setTimeout(() => navigate("/login"), 2000); // Chuyển hướng sau 2 giây
        } else {
          errorMessage = err.response.data?.message || "Không thể tải cấu hình giới hạn chat!";
        }
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
      }
      setError(errorMessage);
      setLoading(false);
    }
  }, [fetchWithRetry, navigate]); // Thêm navigate vào dependencies

  const handleUpdateConfig = useCallback(
    async (role, updatedConfig) => {
      try {
        const res = await fetchWithRetry({
          method: "put",
          url: "/api/admin/roles/chat-restrictions",
          data: { role, ...updatedConfig },
        });
        setNotificationMessage(res.data.message || "Cập nhật cấu hình giới hạn chat thành công!");
        setConfigs((prev) =>
          prev.map((config) =>
            config.role === role ? { ...config, ...res.data.config } : config
          )
        );
        setTimeout(() => setNotificationMessage(null), 3000);
      } catch (err) {
        console.error("Lỗi khi cập nhật cấu hình giới hạn chat:", err);
        let errorMessage = "Không thể cập nhật cấu hình giới hạn chat!";
        if (err.response) {
          if (err.response.status === 500) {
            errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
          } else if (err.response.status === 401) {
            errorMessage = "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.";
          } else if (err.response.status === 403) {
            errorMessage = "Bạn không có quyền thực hiện hành động này!";
          } else {
            errorMessage = err.response.data?.message || "Không thể cập nhật cấu hình giới hạn chat!";
          }
        } else if (err.message.includes("Network Error")) {
          errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
        }
        setError(errorMessage);
      }
    },
    [fetchWithRetry]
  );

  const handleToggleGlobal = useCallback(async () => {
    try {
      const newEnabled = !globalEnabled;
      const res = await fetchWithRetry({
        method: "put",
        url: "/api/admin/roles/toggle-chat-restrictions",
        data: { enabled: newEnabled },
      });
      setNotificationMessage(res.data.message || `Giới hạn chat đã được ${newEnabled ? "bật" : "tắt"}!`);
      setGlobalEnabled(newEnabled);
      setConfigs((prev) =>
        prev.map((config) => ({ ...config, enabled: newEnabled }))
      );
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (err) {
      console.error("Lỗi khi bật/tắt giới hạn chat:", err);
      let errorMessage = "Không thể bật/tắt giới hạn chat!";
      if (err.response) {
        if (err.response.status === 500) {
          errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
        } else if (err.response.status === 401) {
          errorMessage = "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.";
        } else if (err.response.status === 403) {
          errorMessage = "Bạn không có quyền thực hiện hành động này!";
        } else {
          errorMessage = err.response.data?.message || "Không thể bật/tắt giới hạn chat!";
        }
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
      }
      setError(errorMessage);
    }
  }, [fetchWithRetry, globalEnabled]);

  const handleToggleHideMessagesWithUser = useCallback(async () => {
    try {
      const newEnabled = !configs.some(config => config.role !== "user" && config.hideMessagesWithUser);
      const res = await fetchWithRetry({
        method: "put",
        url: "/api/admin/roles/toggle-hide-messages-with-user",
        data: { enabled: newEnabled },
      });
      setNotificationMessage(res.data.message || `Ẩn tin nhắn (nếu một trong hai là user) đã được ${newEnabled ? "bật" : "tắt"}!`);
      setConfigs((prev) =>
        prev.map((config) =>
          config.role !== "user" ? { ...config, hideMessagesWithUser: newEnabled } : config
        )
      );
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (err) {
      console.error("Lỗi khi bật/tắt ẩn tin nhắn với user:", err);
      let errorMessage = "Không thể bật/tắt ẩn tin nhắn với user!";
      if (err.response) {
        if (err.response.status === 500) {
          errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
        } else if (err.response.status === 401) {
          errorMessage = "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.";
        } else if (err.response.status === 403) {
          errorMessage = "Bạn không có quyền thực hiện hành động này!";
        } else {
          errorMessage = err.response.data?.message || "Không thể bật/tắt ẩn tin nhắn với user!";
        }
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
      }
      setError(errorMessage);
    }
  }, [fetchWithRetry, configs]);

  useEffect(() => {
    if (isLoading) return;

    if (!user || !user.permissions.includes("manage_chat_restrictions")) {
      setError("Bạn không có quyền truy cập trang này!");
      setLoading(false);
      return;
    }

    fetchConfigs();

    const socket = getSocket();
    if (!socket) {
      console.warn("Socket chưa sẵn sàng, bỏ qua lắng nghe thông báo");
      setError("Không thể kết nối tới server thông báo. Vui lòng thử lại!");
      return;
    }

    socket.on("connect_error", (err) => {
      setError("Không thể kết nối tới server thông báo. Vui lòng thử lại!");
      console.error("Socket.IO connect error:", err);
    });

    socket.on("notification", (notification) => {
      if (notification.type === "chat_restriction_updated") {
        setNotificationMessage(notification.message || "Cấu hình giới hạn chat đã được cập nhật!");
        fetchConfigs();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
      if (notification.type === "chat_restrictions_toggled") {
        setGlobalEnabled(notification.enabled);
        setNotificationMessage(notification.message || `Giới hạn chat đã được ${notification.enabled ? "bật" : "tắt"}!`);
        fetchConfigs();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
      if (notification.type === "hide_messages_with_user_toggled") {
        setNotificationMessage(notification.message || `Ẩn tin nhắn (nếu một trong hai là user) đã được ${notification.enabled ? "bật" : "tắt"}!`);
        setConfigs((prev) =>
          prev.map((config) =>
            config.role !== "user" ? { ...config, hideMessagesWithUser: notification.enabled } : config
          )
        );
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
      socket.off("connect_error");
    };
  }, [user, fetchConfigs, isLoading]);

  if (loading) return <p className="text-center text-gray-600">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50 font-sans">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Cấu hình giới hạn chat</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <div className="flex gap-2">
            {error.includes("Phiên làm việc hết hạn") ? (
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
              >
                Đăng nhập lại
              </button>
            ) : (
              <>
                <button
                  onClick={() => fetchConfigs()}
                  className="px-4 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                >
                  Thử lại
                </button>
                <button
                  onClick={() => setError(null)}
                  className="px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
                >
                  Đóng
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={globalEnabled}
              onChange={handleToggleGlobal}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Bật toàn bộ giới hạn chat</span>
          </label>
        </div>
        <div className="mb-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={configs.some(config => config.role !== "user" && config.hideMessagesWithUser)}
              onChange={handleToggleHideMessagesWithUser}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Ẩn tin nhắn (nếu một trong hai là user) cho các vai trò VIP/quản trị</span>
          </label>
        </div>
        
          <div className="space-y-6">
            {roles.map((role) => {
              const config = configs.find((c) => c.role === role) || {
                role,
                enabled: globalEnabled,
                maxMessages: 0,
                videoSupport: false,
                priority: false,
                virtualGifts: false,
                hideMessages: role === "user",
                hideMessagesWithUser: role !== "user",
                maxPinnedMessages: 5, // Giá trị mặc định
              };
              return (
                <div key={role} className="border p-4 rounded-lg bg-gray-50">
                  <h3 className="text-lg font-semibold capitalize text-gray-800">{role}</h3>
                  <div className="mt-4 space-y-4">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) =>
                          handleUpdateConfig(role, {
                            ...config,
                            enabled: e.target.checked,
                          })
                        }
                        className="form-checkbox h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Kích hoạt giới hạn</span>
                    </label>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Số tin nhắn tối đa mỗi ngày
                      </label>
                      <input
                        type="number"
                        value={config.maxMessages}
                        onChange={(e) =>
                          handleUpdateConfig(role, {
                            ...config,
                            maxMessages: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        disabled={!config.enabled}
                        min="0"
                      />
                    </div>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={config.videoSupport}
                        onChange={(e) =>
                          handleUpdateConfig(role, {
                            ...config,
                            videoSupport: e.target.checked,
                          })
                        }
                        className="form-checkbox h-5 w-5 text-blue-600"
                        disabled={!config.enabled}
                      />
                      <span className="ml-2 text-gray-700">Hỗ trợ video</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={config.priority}
                        onChange={(e) =>
                          handleUpdateConfig(role, {
                            ...config,
                            priority: e.target.checked,
                          })
                        }
                        className="form-checkbox h-5 w-5 text-blue-600"
                        disabled={!config.enabled}
                      />
                      <span className="ml-2 text-gray-700">Ưu tiên</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={config.virtualGifts}
                        onChange={(e) =>
                          handleUpdateConfig(role, {
                            ...config,
                            virtualGifts: e.target.checked,
                          })
                        }
                        className="form-checkbox h-5 w-5 text-blue-600"
                        disabled={!config.enabled}
                      />
                      <span className="ml-2 text-gray-700">Quà tặng ảo</span>
                    </label>
                    {/* Tùy chọn ẩn tin nhắn cho vai trò "user" */}
                    {role === "user" && (
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={config.hideMessages}
                          onChange={(e) =>
                            handleUpdateConfig(role, {
                              ...config,
                              hideMessages: e.target.checked,
                            })
                          }
                          className="form-checkbox h-5 w-5 text-blue-600"
                          disabled={!config.enabled}
                        />
                        <span className="ml-2 text-gray-700">Ẩn tin nhắn (nếu cả hai là user)</span>
                      </label>
                    )}
                    {/* Tùy chọn ẩn tin nhắn với user cho các vai trò VIP/quản trị */}
                    {role !== "user" && (
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={config.hideMessagesWithUser}
                          onChange={(e) =>
                            handleUpdateConfig(role, {
                              ...config,
                              hideMessagesWithUser: e.target.checked,
                            })
                          }
                          className="form-checkbox h-5 w-5 text-blue-600"
                          disabled={!config.enabled}
                        />
                        <span className="ml-2 text-gray-700">Ẩn tin nhắn (nếu một trong hai là user)</span>
                      </label>
                    )}
                  </div>
                  <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700">
            Thời gian cho phép thu hồi tin nhắn (phút)
          </label>
          <input
            type="number"
            value={configs.find(config => config.role === "user")?.recallTimeLimit || 60}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value) || 60;
              handleUpdateConfig("user", {
                ...configs.find(config => config.role === "user"),
                recallTimeLimit: newLimit,
              });
            }}
            className="mt-1 block w-32 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            min="0"
          />
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700">
            Số lượng tin nhắn được ghim tối đa
          </label>
          <input
            type="number"
            value={configs.find(config => config.role === "user")?.maxPinnedMessages || 5}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value) || 5;
              handleUpdateConfig("user", {
                ...configs.find(config => config.role === "user"),
                maxPinnedMessages: newLimit,
              });
            }}
            className="mt-1 block w-32 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            min="0"
          />
        </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ChatRestrictionSettings;